import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { TreeDeciduous, Users, Calendar, Shield, Download, Share2, BookOpen, HelpCircle } from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();

  const handleLogin = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`);
      const data = await response.json();
      window.location.href = data.auth_url;
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  const features = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Family Branches",
      description: "Each branch of our family can add and manage their own information."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Privacy Controls",
      description: "Branch admins control who can see and edit their family data."
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Event Timeline",
      description: "Track births, marriages, and important family milestones."
    },
    {
      icon: <Download className="w-6 h-6" />,
      title: "Export Data",
      description: "Download our complete family tree data anytime."
    },
    {
      icon: <Share2 className="w-6 h-6" />,
      title: "Invite Relatives",
      description: "Share invite links to bring more family members on board."
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Historical Records",
      description: "Preserve our family history and stories for future generations."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TreeDeciduous className="w-8 h-8 text-primary" />
            <span className="font-display text-xl font-bold text-foreground">Matambanadzo-Gurundoro</span>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost"
              onClick={() => navigate("/help")}
              data-testid="nav-help-btn"
              className="text-foreground hover:text-primary"
            >
              <HelpCircle className="w-5 h-5 mr-1" />
              Help
            </Button>
            <Button 
              onClick={handleLogin}
              data-testid="nav-login-btn"
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6"
            >
              Sign In
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1640690642852-cae8cdf55cde?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxsaW9uJTIwZmFtaWx5JTIwZ3JvdXAlMjBjdWJzJTIwYWZyaWNhfGVufDB8fHx8MTc3MzE3NDE4NHww&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/70 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 md:py-32 lg:py-40">
          <div className="max-w-3xl">
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-foreground tracking-tight mb-6 animate-fade-in">
              Our Family Tree
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 animate-fade-in" style={{ animationDelay: '100ms' }}>
              Welcome to the Matambanadzo-Gurundoro family archive. Each branch of our family can contribute their stories, photos, and memories — together we preserve our heritage.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in" style={{ animationDelay: '200ms' }}>
              <Button 
                onClick={handleLogin}
                data-testid="hero-get-started-btn"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
              >
                Sign In to Continue
              </Button>
              <Button 
                variant="outline"
                data-testid="hero-learn-more-btn"
                className="border-2 border-primary text-primary hover:bg-primary/5 rounded-full px-8 py-6 text-lg font-medium"
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-28 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-foreground mb-4">
              What You Can Do
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Tools to help us preserve and share our family heritage together
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-card text-card-foreground rounded-2xl border border-border/60 p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary/30 hover:ring-1 hover:ring-primary/30 card-hover"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-5">
                  {feature.icon}
                </div>
                <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-foreground mb-6">
            Preserving Our Legacy Together
          </h2>
          <p className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
            Sign in to add your branch's information, view our family history, and help keep our heritage alive for future generations.
          </p>
          <Button 
            onClick={handleLogin}
            data-testid="cta-get-started-btn"
            className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-10 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
          >
            Enter Family Archive
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <TreeDeciduous className="w-6 h-6" />
              <span className="font-display text-xl font-bold">Matambanadzo-Gurundoro</span>
            </div>
            <p className="text-primary-foreground/70 text-sm">
              © 2025 Matambanadzo-Gurundoro Family. Our roots run deep.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
